// react and js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// English is bundled with the initial JS chunk so first paint is never blocked
// on a network round-trip. All other locales are dynamic imports (one chunk
// per locale, lazy-loaded the first time the user selects that language).
import enTranslation from "./locales/en/translation.json";

// ----------------------------------------------------------------------
// Supported languages
// ----------------------------------------------------------------------
// The order here drives the language-picker dropdown in PublicHeader.
export const SUPPORTED_LANGUAGES = [
  { code: "en",    nativeName: "English" },
  { code: "de",    nativeName: "Deutsch" },
  { code: "fr",    nativeName: "Français" },
  { code: "es",    nativeName: "Español" },
  { code: "zh-CN", nativeName: "简体中文" },
  { code: "zh-TW", nativeName: "繁體中文" },
  { code: "ja",    nativeName: "日本語" },
  { code: "pt-BR", nativeName: "Português" },
  { code: "ar",    nativeName: "العربية" },
  { code: "ko",    nativeName: "한국어" },
  { code: "pl",    nativeName: "Polski" },
];

export const DEFAULT_LANGUAGE = "en";

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

const RTL_LANGUAGES = new Set(["ar"]);

// Case-insensitive lookup that returns the canonical code (e.g. "ZH-cn" → "zh-CN").
function normalizeLangSegment(segment) {
  if (!segment) return null;
  const lower = String(segment).toLowerCase();
  return SUPPORTED_CODES.find((c) => c.toLowerCase() === lower) || null;
}

// ----------------------------------------------------------------------
// URL helpers
// ----------------------------------------------------------------------
// Extract the language code from a pathname's first segment.
// Returns the canonical code, or null if the segment is not a known language.
// "/" or "/distribution" → null  (English, no prefix)
// "/de" or "/de/distribution" → "de"
// "/zh-CN/promotion" → "zh-CN"
export function getLangFromPath(pathname) {
  if (!pathname) return null;
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;
  return normalizeLangSegment(segments[0]);
}

// Returns the pathname with the language segment stripped (and a leading slash).
// "/de/distribution" → "/distribution"
// "/distribution" → "/distribution"
// "/de" → "/"
export function stripLangFromPath(pathname) {
  if (!pathname) return "/";
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  if (normalizeLangSegment(segments[0])) {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }
  return pathname;
}

// Build a pathname for the given language, preserving the rest of the path.
// buildPathForLang("de", "/distribution") → "/de/distribution"
// buildPathForLang("en", "/de/distribution") → "/distribution"
export function buildPathForLang(lang, pathname) {
  const rest = stripLangFromPath(pathname);
  if (!lang || lang === DEFAULT_LANGUAGE) return rest;
  if (rest === "/") return `/${lang}`;
  return `/${lang}${rest}`;
}

export function isSupportedLanguage(code) {
  return normalizeLangSegment(code) !== null;
}

export function isRtlLanguage(code) {
  return RTL_LANGUAGES.has(code);
}

// Insert the active language as a path prefix on a website URL.
// Assumes the input URL is the English (default) version, e.g.
//   https://www.fea-trading.com/imprint  +  "de"  -> https://www.fea-trading.com/de/imprint
//   https://www.fea-trading.com          +  "de"  -> https://www.fea-trading.com/de
//   https://www.fea-trading.com/imprint  +  "en"  -> https://www.fea-trading.com/imprint  (unchanged)
// Query strings and hashes are preserved. Returns the input unchanged if the
// URL can't be parsed or no language is given.
export function localizeWebsiteUrl(url, lang) {
  if (!url) return url;
  if (!lang || lang === DEFAULT_LANGUAGE) return url;
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname;
    u.pathname = `/${lang}${path}`;
    return u.toString();
  } catch {
    return url;
  }
}

// ----------------------------------------------------------------------
// Lazy locale loading
// ----------------------------------------------------------------------
// Dynamic import keyed by language code. Each call returns a Promise that
// resolves once the translation JSON has been loaded and registered with
// i18next. Subsequent calls for the same language are no-ops.
const loadedLocales = new Set([DEFAULT_LANGUAGE]);

export async function ensureLanguageLoaded(code) {
  const normalized = normalizeLangSegment(code) || DEFAULT_LANGUAGE;
  if (loadedLocales.has(normalized)) return normalized;

  let resource;
  // Webpack needs a literal-ish path inside the dynamic import to generate
  // chunks, so we list the locales explicitly.
  switch (normalized) {
    case "de":    resource = await import("./locales/de/translation.json"); break;
    case "fr":    resource = await import("./locales/fr/translation.json"); break;
    case "es":    resource = await import("./locales/es/translation.json"); break;
    case "pt-BR": resource = await import("./locales/pt-BR/translation.json"); break;
    case "pl":    resource = await import("./locales/pl/translation.json"); break;
    case "ja":    resource = await import("./locales/ja/translation.json"); break;
    case "ko":    resource = await import("./locales/ko/translation.json"); break;
    case "zh-CN": resource = await import("./locales/zh-CN/translation.json"); break;
    case "zh-TW": resource = await import("./locales/zh-TW/translation.json"); break;
    case "ar":    resource = await import("./locales/ar/translation.json"); break;
    default: return DEFAULT_LANGUAGE;
  }

  i18n.addResourceBundle(
    normalized,
    "translation",
    resource.default || resource,
    true,
    true
  );
  loadedLocales.add(normalized);
  return normalized;
}

// ----------------------------------------------------------------------
// Initialise i18next
// ----------------------------------------------------------------------
// The initial language is read from the URL path so server-rendered links
// (and copy-pasted URLs) render in the correct language on first paint.
const initialLang =
  (typeof window !== "undefined" && getLangFromPath(window.location.pathname)) ||
  DEFAULT_LANGUAGE;

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: enTranslation },
  },
  lng: initialLang,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_CODES,
  interpolation: { escapeValue: false }, // React already escapes
  returnEmptyString: false,
  react: { useSuspense: false },         // we lazy-load manually below
});

// If the initial URL points at a non-English locale, kick off its load.
// React components render with English fallbacks until the bundle arrives,
// then re-render with the translated strings.
if (initialLang !== DEFAULT_LANGUAGE) {
  ensureLanguageLoaded(initialLang).then(() => {
    i18n.changeLanguage(initialLang);
  });
}

export default i18n;
